export const DEMON_TERMS = [
  { id: '1m', label: '1 Month', months: 1, perConnection: 4 },
  { id: '3m', label: '3 Months', months: 3, perConnection: 11 },
  { id: '6m', label: '6 Months', months: 6, perConnection: 17 },
  { id: '1y', label: '1 Year', months: 12, perConnection: 28 },
  { id: '2y', label: '2 Years', months: 24, perConnection: 45 },
  { id: '3y', label: '3 Years', months: 36, perConnection: 60 },
];

export const WRAITH_TERMS = [
  { id: '1m', label: '1 Month', months: 1, price: 7, epg: false },
  { id: '3m', label: '3 Months', months: 3, price: 15, epg: false },
  { id: '6m', label: '6 Months', months: 6, price: 25, epg: true },
  { id: '12m', label: '12 Months', months: 12, price: 40, epg: true },
];

export const BANSHEE_TERMS = [
  { id: '1m', label: '1 Month', months: 1, prices: [15, 20, 25, 30, 35] },
  { id: '3m', label: '3 Months', months: 3, prices: [30, 40, 45, 50, 55] },
  { id: '6m', label: '6 Months', months: 6, prices: [50, 60, 70, 80, 90] },
  { id: '1y', label: '1 Year', months: 12, prices: [80, 100, 115, 130, 145] },
];

export const BANSHEE_BADGES = [
  'Lowest price',
  'Save more',
  'Most popular',
  'Perfect for families',
  'Ultimate deal',
];

export const BANSHEE_PATH = '/banshee-7x2q9k';
/** Private comparison of Wraith, Demon and Banshee. Same privacy as the Banshee page. */
export const BANSHEE_COMPARE_PATH = `${BANSHEE_PATH}/compare`;
export const MAX_CONNECTIONS = 5;
export const TRIAL_HOURS = 24;
export const RENEW_WARN_DAYS = 7;

export const TELLY = {
  price: 7,
  name: 'Desktop Telly',
  term: 'lifetime',
  termLabel: 'Lifetime licence',
  url: 'https://desktoptelly.com/',
  maxQty: 10,
  points: [
    'Lifetime licence, pay once',
    'Active on one device at a time',
    'Move it to a new device whenever you like',
    'Windows, macOS, Linux, Android and Apple TV',
  ],
};

export const TELLY_TERMS = [{ id: 'lifetime', label: 'Lifetime licence', months: 0 }];

/** Terms a custom product can be sold on. Each reads its price from the product record. */
export const PRODUCT_TERMS = [
  { id: '1m', label: '1 Month', months: 1, field: 'price_1m_c' },
  { id: '3m', label: '3 Months', months: 3, field: 'price_3m_c' },
  { id: '6m', label: '6 Months', months: 6, field: 'price_6m_c' },
  { id: '12m', label: '12 Months', months: 12, field: 'price_12m_c' },
  { id: 'lifetime', label: 'One-time', months: 0, field: 'price_once_c' },
];

/** Term id of a product sold for a single payment. It never ends and never renews. */
export const ONE_TIME_TERM = 'lifetime';

/** A custom product order paid once (for example Wraith EPG). */
export const isOneTimeOrder = (order) => order?.plan_c === 'Product' && order?.term_c === ONE_TIME_TERM;

/** Price of a product on a term, or null when that term is not offered. */
export function productPrice(product, term) {
  const t = PRODUCT_TERMS.find((x) => x.id === term);
  if (!product || !t) return null;
  const v = Number(product[t.field] || 0);
  return v > 0 ? v : null;
}

/** The terms a product actually sells, with their prices. */
export function productTerms(product) {
  return PRODUCT_TERMS.map((t) => ({ ...t, price: productPrice(product, t.id) })).filter((t) => t.price != null);
}

/** Plans built into the site that can be managed as product rows. */
export const BUILT_IN_KEYS = ['Demon', 'Wraith', 'Telly', 'Banshee'];

export const PLAN_VALUES = ['Demon', 'Wraith', 'Banshee', 'Telly'];
export const ORDER_PLAN_VALUES = [...PLAN_VALUES, 'Product'];
export const TERM_VALUES = ['1m', '3m', '6m', '1y', '12m', '2y', '3y', 'lifetime'];

/** What each plan includes (Demon runs on V12 OTT, Wraith on Strong 8K). Screen count is added per plan. */
export const DEMON_FEATURES = [
  '23,000+ live channels',
  '147,000+ movies and VOD',
  '4K, FHD, HD and SD quality',
  'EPG guide, catch-up TV and PPV events',
  'All supported devices',
];

export const WRAITH_FEATURES = [
  '46,000+ live channels',
  '180,000+ movies and VOD',
  '4K, FHD, HD and SD quality',
  'Catch-up TV and PPV events',
];

export const PLANS = {
  Demon: {
    name: 'Demon',
    tagline: 'Possess every screen in the house',
    blurb:
      'Stream on 1 to 5 devices at the same time, so the whole house can watch what they want. Over 23,000 live channels and a 147,000-title movie and VOD library, in 4K, FHD, HD and SD. The TV guide (EPG), catch-up TV and PPV events are all included. Lock in a long term for the deepest savings.',
  },
  Wraith: {
    name: 'Wraith',
    tagline: 'One quiet presence, always watching',
    blurb:
      'A single connection at the lowest price, with no compromise on what you can watch. Over 46,000 live channels and a 180,000-title movie and VOD library, streaming in 4K, FHD, HD and SD. Catch up on shows you missed, and catch the big PPV events live. The premium TV guide (EPG) is included with 6 and 12 month packages.',
  },
  Banshee: {
    name: 'Banshee',
    tagline: 'Her wail reaches every room',
    blurb: 'Private packages from 1 to 5 simultaneous connections, by invitation only.',
  },
  Telly: {
    name: 'Desktop Telly',
    tagline: 'A proper player for every screen',
    blurb: 'Lifetime Desktop Telly licence. Active on one device at a time and transferable between devices.',
  },
  Product: {
    name: 'Product',
    tagline: 'Something new from the house',
    blurb: 'A product listed in the store.',
  },
};

export const planName = (plan) => PLANS[plan]?.name ?? plan ?? '';

export const CONTACT_METHODS = [
  { id: 'Discord', placeholder: 'phantom#0666' },
  { id: 'WhatsApp', placeholder: '+1 555 013 6666' },
  { id: 'Telegram', placeholder: '@phantomviewer' },
];

export const contactPlaceholder = (method) =>
  CONTACT_METHODS.find((c) => c.id === method)?.placeholder ?? 'Your handle';

export const PAYMENT_METHODS = [
  { value: 'Card', label: 'Credit Card' },
  { value: 'Wise', label: 'Wise' },
  { value: 'Crypto', label: 'Crypto' },
  { value: 'E-Transfer', label: 'E-Transfer (CAD)' },
];

export const paymentLabel = (value) =>
  PAYMENT_METHODS.find((p) => p.value === value)?.label ?? value ?? '';

/** Methods no longer offered. Older orders and payments may still carry them. */
export const RETIRED_PAYMENT_METHODS = ['PayPal'];

/** The methods buyers can pick at checkout (the same for every plan). */
export const paymentMethodsFor = () => PAYMENT_METHODS;

/** Method values for a team form: the offered ones, plus a retired one the record already has. */
export const methodValuesWith = (current) => {
  const values = PAYMENT_METHODS.map((m) => m.value);
  return current && !values.includes(current) ? [...values, current] : values;
};

export function termsFor(plan) {
  if (plan === 'Demon') return DEMON_TERMS;
  if (plan === 'Banshee') return BANSHEE_TERMS;
  if (plan === 'Telly') return TELLY_TERMS;
  if (plan === 'Product') return PRODUCT_TERMS;
  return WRAITH_TERMS;
}

/** Price in whole USD, or null when the combination is not sold. */
export function quote(plan, term, connections) {
  if (plan === 'Product') return null;
  const n = Number(connections);
  if (plan === 'Demon') {
    const t = DEMON_TERMS.find((x) => x.id === term);
    if (!t || n < 1 || n > MAX_CONNECTIONS) return null;
    return t.perConnection * n;
  }
  if (plan === 'Telly') {
    return term === TELLY.term && n >= 1 && n <= TELLY.maxQty ? TELLY.price * n : null;
  }
  if (plan === 'Banshee') {
    const t = BANSHEE_TERMS.find((x) => x.id === term);
    return t?.prices[n - 1] ?? null;
  }
  const t = WRAITH_TERMS.find((x) => x.id === term);
  if (!t || n !== 1) return null;
  return t.price;
}

/** Every price a built-in plan sells at, in USD (used for search structured data). */
export function planPriceList(plan) {
  if (plan === 'Demon') {
    return DEMON_TERMS.flatMap((t) => Array.from({ length: MAX_CONNECTIONS }, (_, i) => t.perConnection * (i + 1)));
  }
  if (plan === 'Wraith') return WRAITH_TERMS.map((t) => t.price);
  if (plan === 'Telly') return [TELLY.price];
  if (plan === 'Banshee') return BANSHEE_TERMS.flatMap((t) => t.prices);
  return [];
}

export function termLabel(plan, term) {
  const own = termsFor(plan).find((x) => x.id === term);
  const any = [...DEMON_TERMS, ...WRAITH_TERMS, ...TELLY_TERMS].find((x) => x.id === term);
  return (own ?? any)?.label ?? term ?? '';
}

export function termMonths(plan, term) {
  return termsFor(plan).find((t) => t.id === term)?.months ?? 1;
}

export function addMonths(from, months) {
  const d = new Date(from);
  d.setMonth(d.getMonth() + months);
  return d;
}

/** When a fresh term started at `from` ends. null = never (lifetime licence). */
export function termEnd(plan, term, from = new Date()) {
  const m = termMonths(plan, term);
  return m > 0 ? addMonths(from, m) : null;
}

/** "3 days left", "5 hours left", "Ended". */
export function timeLeft(endsAt, now = Date.now()) {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return 'Ended';
  const h = ms / 3600000;
  if (h < 1) return `${Math.max(1, Math.round(ms / 60000))} min left`;
  if (h < 48) {
    const hours = Math.round(h);
    return `${hours} hour${hours === 1 ? '' : 's'} left`;
  }
  return `${Math.round(h / 24)} days left`;
}

/** Trials always, paid terms inside the warning window, anything that lapsed. */
export function needsRenewal(order, now = Date.now()) {
  const ends = order?.ends_at_c;
  const status = order?.status_c;
  if (!ends || (status !== 'Active' && status !== 'Trial')) return false;
  if (status === 'Trial') return true;
  return new Date(ends).getTime() - now <= RENEW_WARN_DAYS * 86400000;
}

export const isLapsed = (order, now = Date.now()) =>
  order?.status_c === 'Active' && !!order?.ends_at_c && new Date(order.ends_at_c).getTime() < now;

export const tellyCount = (order) => {
  if (order?.plan_c === 'Telly') return Number(order.connections_c || 1);
  return order?.telly_addon_c ? Math.max(1, Number(order.telly_qty_c || 1)) : 0;
};

/** A renewal costs the plan only: the Telly add-on is a lifetime licence, paid once. */
export const renewalPrice = (order) => {
  if (order?.plan_c === 'Telly') return 0;
  const telly = order?.telly_addon_c ? Math.max(1, Number(order.telly_qty_c || 1)) * TELLY.price : 0;
  return Number(order?.price_c || 0) - telly;
};

export const orderTotal = (order) =>
  Math.max(0, Number(order?.price_c || 0) - Number(order?.discount_c || 0));

export function money(n) {
  const v = Math.round(Number(n || 0) * 100) / 100;
  return `$${Number.isInteger(v) ? v : v.toFixed(2)}`;
}

export function planSummary(order) {
  if (!order) return '';
  const n = Number(order.connections_c || 1);
  if (order.plan_c === 'Telly') return `Desktop Telly · ${n} licence${n === 1 ? '' : 's'}`;
  if (order.plan_c === 'Product') return `${order.product_c?.Name ?? 'Product'} · ${termLabel('Product', order.term_c)}`;
  return `${planName(order.plan_c)} · ${termLabel(order.plan_c, order.term_c)} · ${n} connection${n === 1 ? '' : 's'}`;
}

/** Best "longer term" and "+1 connection" offers for a selection { plan, term, connections }. */
export function upsellOffers(sel) {
  if (!sel || sel.plan === 'Telly' || sel.plan === 'Product') return [];
  const price = quote(sel.plan, sel.term, sel.connections);
  if (price == null) return [];
  const terms = termsFor(sel.plan);
  const cur = terms.find((t) => t.id === sel.term);
  const out = [];

  const longer = cur && terms.find((t) => t.months > cur.months);
  const longerPrice = longer ? quote(sel.plan, longer.id, sel.connections) : null;
  if (cur && longer && longerPrice != null) {
    const save = (price / cur.months) * longer.months - longerPrice;
    if (save >= 1) {
      const addsEpg =
        sel.plan === 'Wraith' &&
        !WRAITH_TERMS.find((t) => t.id === cur.id)?.epg &&
        !!WRAITH_TERMS.find((t) => t.id === longer.id)?.epg;
      out.push({
        kind: 'term',
        next: { ...sel, term: longer.id },
        title: `Switch to ${longer.label} and save ${money(Math.round(save))}`,
        detail: `${money(longerPrice / longer.months)}/mo instead of ${money(price / cur.months)}/mo${addsEpg ? ' · adds the TV guide (EPG)' : ''}`,
        tKey: 'Switch to {term} and save {amount}',
        tVars: { term: longer.label, amount: money(Math.round(save)) },
        dKey: '{per}/mo instead of {was}/mo',
        dVars: { per: money(longerPrice / longer.months), was: money(price / cur.months) },
        dEpg: addsEpg,
      });
    }
  }

  if ((sel.plan === 'Demon' || sel.plan === 'Banshee') && sel.connections < MAX_CONNECTIONS) {
    const morePrice = quote(sel.plan, sel.term, sel.connections + 1);
    if (morePrice != null) {
      out.push({
        kind: 'conn',
        next: { ...sel, connections: sel.connections + 1 },
        title: `Add a connection for +${money(morePrice - price)}`,
        detail: `${sel.connections + 1} screens streaming at the same time`,
        tKey: 'Add a connection for +{amount}',
        tVars: { amount: money(morePrice - price) },
        dKey: '{count} screens streaming at the same time',
        dVars: { count: sel.connections + 1 },
      });
    }
  }
  return out;
}

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function newCode(prefix = 'GC-', length = 6) {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return `${prefix}${out}`;
}

/** Discount in USD for a code record against a price, or 0 when it does not apply. */
export function discountFor(code, plan, price) {
  if (!code || !price) return 0;
  if (code.plan_c && code.plan_c !== 'All' && code.plan_c !== plan) return 0;
  const value = Number(code.value_c || 0);
  const raw = code.kind_c === 'Percent' ? (price * value) / 100 : value;
  return Math.round(Math.min(raw, price) * 100) / 100;
}
